import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { register as registerClientExtensions } from 'virtual:bw-client-extensions';
import { App } from './App';
import { extensionRegistry } from './lib/extensions/registry';
import { APP_TITLE } from './lib/brand';
import './lib/i18n'; // initialize i18next before React mounts

// Variable Inter font (Greek subset ships automatically).
import '@fontsource-variable/inter';
import '@fontsource-variable/inter/wght-italic.css';
// Variable JetBrains Mono for code/ID pills (latin subset is enough — IDs are ASCII).
import '@fontsource-variable/jetbrains-mono';
import './styles/globals.css';

// Theme bootstrap before React mounts (avoids a flash of the wrong theme). Reads the
// zustand `persist` envelope ({"state":{"theme":"dark"},...}). A legacy raw-string value
// from the pre-persist store is migrated into the envelope so `persist` rehydrates it and
// the store and DOM stay in agreement.
function readStoredTheme(): 'light' | 'dark' {
	const raw = localStorage.getItem('admin-v2-theme');
	if (!raw) return 'light';
	try {
		const parsed = JSON.parse(raw);
		if (parsed?.state?.theme) return parsed.state.theme === 'dark' ? 'dark' : 'light';
	} catch {
		/* legacy raw value handled below */
	}
	const legacy: 'light' | 'dark' = raw === 'dark' ? 'dark' : 'light';
	localStorage.setItem('admin-v2-theme', JSON.stringify({ state: { theme: legacy }, version: 0 }));
	return legacy;
}
document.documentElement.setAttribute('data-theme', readStoredTheme());

// Brand the tab title before React mounts (and before AppShell sets per-route titles),
// so the login screen and initial paint show the tenant name instead of the static default.
document.title = APP_TITLE;

// Register the repo's client-specific UI extensions (e.g. tenant order tools) into the shared
// registry before React mounts. No-ops in repos without an admin-v2-ext package.
registerClientExtensions(extensionRegistry);

const queryClient = new QueryClient({
	defaultOptions: { queries: { staleTime: 30_000 } }
});

createRoot(document.getElementById('root')!).render(
	<StrictMode>
		<QueryClientProvider client={queryClient}>
			<BrowserRouter>
				<App />
			</BrowserRouter>
		</QueryClientProvider>
	</StrictMode>
);
